import { ElementRef } from '@angular/core'
import {
	RemoteAudioTrack,
	RemoteParticipant,
	RemoteTrack,
	RemoteVideoTrack,
} from 'twilio-video'
import { Nullable } from '../models/nullable'

/**
 * Attaches all attachable published tracks from the remote participant.
 *
 * @param publications
 * The list of possible publications to attach.
 */
export function attachAttachableTracksForRemoteParticipant(
	participant: RemoteParticipant,
	remoteMediaContainer?: ElementRef
) {
	participant.tracks.forEach((publication) => {
		if (!publication.isSubscribed) return

		if (!trackExistsAndIsAttachable(publication.track)) return

		attachTrack(publication.track, remoteMediaContainer)
	})
}

/**
 * Attaches a remote track.
 *
 * @param track
 * The remote track to attach.
 */
export function attachTrack(
	track: RemoteAudioTrack | RemoteVideoTrack,
	remoteMediaContainer?: ElementRef
) {
	const container = remoteMediaContainer?.nativeElement
	if (!container) return

	const attachedElement = track.attach()

	if (track.kind === 'video' || track.kind === 'audio') {
		const participantSid = track.sid.split('.')[0]
		attachedElement.setAttribute('data-participant-sid', participantSid)
	}

	container.appendChild(attachedElement)

	adjustVideoSizes(container)
}

/**
 * Guard that a track is attachable.
 *
 * @param track
 * The remote track candidate.
 */
export function trackExistsAndIsAttachable(
	track?: Nullable<RemoteTrack>
): track is RemoteAudioTrack | RemoteVideoTrack {
	return (
		!!track &&
		((track as RemoteAudioTrack).attach !== undefined ||
			(track as RemoteVideoTrack).attach !== undefined)
	)
}

/**
 * Triggers when a remote participant connects to the room.
 *
 * @param participant
 * The remote participant
 */
export function onParticipantConnected(
	participant: RemoteParticipant,
	remoteMediaContainer?: ElementRef
) {
	manageTracksForRemoteParticipant(participant, remoteMediaContainer)
}

/**
 * Triggers when a remote participant disconnects from the room.
 *
 * @param participant
 * The remote participant
 */
export function onParticipantDisconnected(
	participant: RemoteParticipant,
	remoteMediaContainer?: ElementRef
) {
	const container = remoteMediaContainer?.nativeElement
	if (!container) return

	const participantElements = container.querySelectorAll(
		`[data-participant-sid="${participant.sid}"]`
	)
	participantElements.forEach((el: any) => el.remove())

	adjustVideoSizes(container)
}

/**
 * Triggers when a remote track is subscribed to.
 *
 * @param track
 * The remote track
 */
export function onTrackSubscribed(
	track: RemoteTrack,
	remoteMediaContainer?: ElementRef
) {
	if (!trackExistsAndIsAttachable(track)) return

	attachTrack(track, remoteMediaContainer)
}

/**
 * Triggers when a remote track is unsubscribed from.
 *
 * @param track
 * The remote track
 */
export function onTrackUnsubscribed(
	track: RemoteTrack,
	remoteMediaContainer?: ElementRef
) {
	if (trackExistsAndIsAttachable(track)) {
		const container = remoteMediaContainer?.nativeElement
		if (!container) return

		const participantSid = track.sid.split('.')[0]
		const participantElements = container.querySelectorAll(
			`[data-participant-sid="${participantSid}"]`
		)

		participantElements.forEach((el: any) => el.remove())

		adjustVideoSizes(container)
	}
}

/**
 * Manages track attachment and subscription for a remote participant.
 *
 * @param participant
 * The remote participant
 */
export function manageTracksForRemoteParticipant(
	participant: RemoteParticipant,
	remoteMediaContainer?: ElementRef
) {
	attachAttachableTracksForRemoteParticipant(
		participant,
		remoteMediaContainer
	)

	participant.on('trackSubscribed', (track) => {
		onTrackSubscribed(track, remoteMediaContainer)
	})

	participant.on('trackUnsubscribed', (track) => {
		onTrackUnsubscribed(track, remoteMediaContainer)
	})
}

function adjustVideoSizes(container: HTMLElement) {
	const videos = container.querySelectorAll('video')
	const count = videos.length

	if (count === 1) {
		videos.forEach((video: any) => {
			video.style.width = '100%'
			video.style.height = '100%'
		})
	} else if (count === 2) {
		videos.forEach((video: any) => {
			video.style.width = '50%'
			video.style.height = '100%'
		})
	} else if (count <= 4) {
		videos.forEach((video: any) => {
			video.style.width = '50%'
			video.style.height = '50%'
		})
	} else {
		videos.forEach((video: any) => {
			video.style.width = '33.33%'
			video.style.height = '33.33%'
		})
	}
}
